import {
  ArrowBackIos,
  ArrowForwardIos,
  Headphones,
  LibraryBooks as LibraryBooksIcon,
  PictureAsPdf,
  PlayCircleOutline,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';

// Types — shared with ContentPage
interface ContentCategory {
  id: string;
  name: string;
  slug: string;
  is_provider_tag?: boolean;
}

interface ContentItem {
  id: string;
  title: string;
  description: string;
  content_type: 'video' | 'audio' | 'pdf' | 'link';
  thumbnail_url: string;
  external_url: string;
  duration_seconds: number | null;
  categories: ContentCategory[];
  published_at: string | null;
  is_pinned: boolean;
  is_new: boolean;
  is_featured: boolean;
}

// Reason: map content type to appropriate MUI icon
function ContentTypeIcon({ type }: { type: string }): React.ReactElement {
  switch (type) {
    case 'video':
      return <PlayCircleOutline sx={{ color: '#FF0000', fontSize: 20 }} />;
    case 'audio':
      return <Headphones sx={{ color: '#1DB954', fontSize: 20 }} />;
    case 'pdf':
      return <PictureAsPdf sx={{ color: '#E53935', fontSize: 20 }} />;
    default:
      return <LibraryBooksIcon sx={{ color: '#666', fontSize: 20 }} />;
  }
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

interface FeaturedCarouselProps {
  items: ContentItem[];
  onItemClick: (id: string) => void;
}

export default function FeaturedCarousel({
  items,
  onItemClick,
}: FeaturedCarouselProps): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const scrollTo = (idx: number): void => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.children[idx] as HTMLElement | undefined;
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      setActiveIdx(idx);
    }
  };

  const handlePrev = (): void => scrollTo(Math.max(0, activeIdx - 1));
  const handleNext = (): void => scrollTo(Math.min(items.length - 1, activeIdx + 1));

  // Reason: sync dot indicator with scroll position on manual swipe
  const handleScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / items.length;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setActiveIdx(idx);
  };

  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <PlayCircleOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No featured content yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search or select a category to browse all content.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {/* Section heading */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'Poppins, system-ui, sans-serif',
          fontWeight: 700,
          color: '#333',
          px: 0.5,
        }}
      >
        Featured
      </Typography>

      {/* Carousel container with nav arrows */}
      <Box sx={{ position: 'relative' }}>
        {/* Left arrow */}
        {items.length > 1 && activeIdx > 0 && (
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: -4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: 2,
              '&:hover': { bgcolor: '#FFF' },
              width: 36,
              height: 36,
            }}
          >
            <ArrowBackIos sx={{ fontSize: 16, ml: 0.5 }} />
          </IconButton>
        )}

        {/* Scrollable cards */}
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            pb: 1,
            px: 0.5,
            /* Reason: hide scrollbar for cleaner look */
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {items.map((item) => {
            const isProviderContent = item.categories.some((c) => c.is_provider_tag);
            return (
            <Box
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => onItemClick(item.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onItemClick(item.id);
                }
              }}
              sx={{
                scrollSnapAlign: 'center',
                flexShrink: 0,
                width: { xs: '85vw', sm: 340 },
                borderRadius: '16px',
                overflow: 'hidden',
                bgcolor: '#F5F5F5',
                border: isProviderContent ? '2px solid #2F80B7' : '2px solid transparent',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  bgcolor: '#E0E0E0',
                  position: 'relative',
                }}
              >
                {item.thumbnail_url ? (
                  <img
                    src={item.thumbnail_url}
                    alt={item.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ContentTypeIcon type={item.content_type} />
                  </Box>
                )}
                {/* Type badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <ContentTypeIcon type={item.content_type} />
                  <Typography sx={{ color: '#FFF', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                    {item.content_type}
                  </Typography>
                </Box>
                {/* NEW badge */}
                {item.is_new && (
                  <Chip
                    label="NEW!"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      height: 20,
                      bgcolor: '#FFD400',
                      color: '#333',
                      fontWeight: 700,
                      '& .MuiChip-label': { px: 0.75, fontSize: 10 },
                    }}
                  />
                )}
              </Box>

              {/* Card body */}
              <Box sx={{ p: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins, system-ui, sans-serif',
                    fontWeight: 600,
                    fontSize: 15,
                    color: '#333',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 0.75,
                  }}
                >
                  {item.title}
                </Typography>
                {item.categories.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {item.categories.map((cat) => (
                      <Chip
                        key={cat.id}
                        label={cat.name}
                        size="small"
                        sx={{
                          height: 20,
                          bgcolor: isProviderContent ? '#2F80B7' : '#FFD400',
                          color: isProviderContent ? '#FFFFFF' : '#333',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 0.75, fontSize: 10 },
                        }}
                      />
                    ))}
                  </Box>
                )}
                {item.duration_seconds ? (
                  <Typography sx={{ fontSize: 12, color: '#828282', mt: 0.5 }}>
                    {formatDuration(item.duration_seconds)}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            );
          })}
        </Box>

        {/* Right arrow */}
        {items.length > 1 && activeIdx < items.length - 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: -4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: 2,
              '&:hover': { bgcolor: '#FFF' },
              width: 36,
              height: 36,
            }}
          >
            <ArrowForwardIos sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* Dot indicators */}
      {items.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75 }}>
          {items.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => scrollTo(idx)}
              sx={{
                width: idx === activeIdx ? 20 : 8,
                height: 8,
                borderRadius: '4px',
                bgcolor: idx === activeIdx ? '#FFD400' : '#D0D0D0',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            />
          ))}
        </Box>
      )}

      {/* Hint text */}
      <Typography
        variant="body2"
        sx={{ textAlign: 'center', color: '#AFAFAF', mt: 1 }}
      >
        Search or select a category to browse all content
      </Typography>
    </Box>
  );
}
